import React from "react";
import PropTypes from "prop-types";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import configSource from "../../res/config.json";

export default class TransferAgreement extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    labels: PropTypes.object,
    switchFormState: PropTypes.func,
    customerInformation: PropTypes.object,
    productsValues: PropTypes.object,
    formInformation: PropTypes.object,
    products: PropTypes.array,
    transferAgreementFile: PropTypes.object,
    accessories: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  };

  state = {
    verify: false,
    pdfURL: null,
    isSigning: false,
    showAttentionDialog: false,
    printFrameLoaded: false,
    printPreviewOpen: false,
  };

  attentionText = {
    fr: {
      header: "Attention !",
      body: "N'installez aucun produit et ne facturez pas HCS tant que le client n'a pas également signé le contrat.",
      button: "J'ai bien compris",
    },
    de: {
      header: "Bitte beachten Sie!",
      body: "Installieren Sie keine Produkte und stellen Sie keine Rechnungen an HCS aus, bevor der Kunde den Vertrag ebenfalls unterzeichnet hat.",
      button: "Ich habe verstanden",
    },
    uk: {
      header: "Warning!",
      body: "Do not install any products or invoice HCS until the customer has also signed the contract.",
      button: "I understand",
    },
  };

  printFrameRef = React.createRef();

  switchFormState = (value) => {
    this.props.switchFormState(value);
  };

  handleChecked = (event) => {
    let newState;
    switch (event.target.id) {
      case "verify":
        newState = this.state.verify ? false : true;
        this.setState({ verify: newState });
        break;
      default:
        throw Error("Unknown button ID");
    }
  };
  handleSign = async () => {
    if (!this.state.verify) return;

    this.setState({ isSigning: true });

    try {
      await this.props.completeTransferAgreementPDF();
    } catch (error) {
      console.error("Signing process failed:", error);
      this.setState({ isSigning: false });
    }
  };

  handlePrint = () => {
    const printFrame = this.printFrameRef.current;

    if (!printFrame || !printFrame.contentWindow) {
      return;
    }

    try {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    } catch (error) {
      this.openPdfInNewTab();
    }
  };

  openPrintPreview = () => {
    this.setState({ printPreviewOpen: true });
  };

  closePrintPreview = () => {
    this.setState({ printPreviewOpen: false });
  };

  openPdfInNewTab = () => {
    const transferAgreementPdfUrl =
      this.props.transferAgreementFile?.fileDisplay;

    if (!transferAgreementPdfUrl) {
      return;
    }

    window.open(transferAgreementPdfUrl, "_blank", "noopener,noreferrer");
  };

  toggleAttentionDialog = () => {
    this.setState({ showAttentionDialog: !this.state.showAttentionDialog });
  };

  handleAttentionConfirm = () => {
    this.setState({ showAttentionDialog: false });
    this.handleSign();
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.transferAgreementFile?.fileDisplay !==
      this.props.transferAgreementFile?.fileDisplay
    ) {
      this.setState({ printFrameLoaded: false });
    }
  }

  fetchTransferAgreementPDF = async () => {
    const country = this.props.params.lang.toLowerCase();
    const pdfName = {
      fr: "Accord_de_transfert.pdf",
    };
    const selectedPdf = pdfName[country];
    const response = await fetch(
      configSource.ROOT +
        `getimage?company=hcs&country=${country}&type=${selectedPdf}`,
    );
    const pdfURL = response.url;
    this.setState({ pdfURL });
  };

  render() {
    const file = this.props.transferAgreementFile;
    const transferAgreementPdfUrl = file?.fileDisplay;
    const lang = this.props.params.lang.toLowerCase();

    return (
      <div>
        <Modal
          open={this.state.showAttentionDialog}
          onClose={this.toggleAttentionDialog}
          aria-labelledby="attention-modal-title"
          aria-describedby="attention-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "35%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "480px",
              bgcolor: "background.paper",
              border: "2px solid #273A60",
              boxShadow: 24,
              borderRadius: 2,
              p: 3,
              textAlign: "center",
            }}
          >
            <WarningAmberRoundedIcon
              sx={{ fontSize: 56, color: "#f59e0b", mb: 1 }}
            />
            <Typography
              id="attention-modal-title"
              variant="h5"
              gutterBottom
              sx={{ fontWeight: "bold", color: "#273A60" }}
            >
              {this.attentionText[lang].header}
            </Typography>
            <Typography
              id="attention-modal-description"
              sx={{ mt: 1, mb: 4, lineHeight: 1.6, fontSize: "18px" }}
            >
              {this.attentionText[lang].body}
            </Typography>
            <Button
              variant="contained"
              size="medium"
              sx={{ backgroundColor: "#273A60", px: 4, mb: 1 }}
              onClick={this.handleAttentionConfirm}
            >
              {this.attentionText[lang].button}
            </Button>
          </Box>
        </Modal>

        {transferAgreementPdfUrl ? (
          <div style={{ textAlign: "center" }}>
            <object
              data={
                transferAgreementPdfUrl +
                "#toolbar=0&navpanes=0&scrollbar=0&view=FitV"
              }
              width="76%"
              height="1000px"
            >
              <p>
                Your browser does not support PDFs. Please download the PDF to
                view it:
                <a href={transferAgreementPdfUrl}>Download PDF</a>
              </p>
            </object>
            <iframe
              ref={this.printFrameRef}
              title="Transfer agreement print frame"
              src={transferAgreementPdfUrl}
              onLoad={() => this.setState({ printFrameLoaded: true })}
              style={{
                border: 0,
                height: 0,
                opacity: 0,
                pointerEvents: "none",
                position: "fixed",
                right: 0,
                top: 0,
                width: 0,
              }}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
            <p>{"Creating transfer agreement PDF..."}</p>
          </div>
        )}

        <Button
          sx={{
            backgroundColor: "#273A60",
            marginLeft: "10px",
          }}
          onClick={this.openPrintPreview}
          variant="contained"
          disabled={!transferAgreementPdfUrl}
        >
          <PrintRoundedIcon sx={{ marginRight: "5px" }} />
          {this.props.labels.print || "Print"}
        </Button>

        <FormControlLabel
          className="quote_order"
          control={
            <Checkbox
              id="verify"
              checked={this.state.verify}
              onChange={this.handleChecked}
              sx={{ width: "10%" }}
            />
          }
          sx={{ width: "100%" }}
          label={this.props.labels.transfer_agreement_verify}
        />

        <div className="button-container">
          <div className="button-container-row">
            <Button
              sx={{ backgroundColor: "#273A60" }}
              onClick={() => this.switchFormState("edit")}
              variant="contained"
            >
              <ArrowBackRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.edit_form}
            </Button>
            {this.state.isSigning ? (
              <div
                style={{
                  textAlign: "center",
                  width: "25%",
                }}
              >
                <CircularProgress size={25} color="primary" />
              </div>
            ) : (
              <Button
                sx={{
                  minWidth: "350px",
                  minHeight: "45px",
                  backgroundColor: "#273A60",
                  marginLeft:
                    this.props.params.formType.toLowerCase() === "leaseplus"
                      ? "auto"
                      : "0",
                }}
                onClick={this.toggleAttentionDialog}
                variant="contained"
                disabled={!this.state.verify}
              >
                <ArrowForwardRoundedIcon sx={{ marginLeft: "300px" }} />
                <span className="app-span-text">
                  {this.props.labels.preview_form_sign}
                </span>
              </Button>
            )}
          </div>
        </div>
        <Dialog
          open={this.state.printPreviewOpen}
          onClose={this.closePrintPreview}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle sx={{ paddingRight: "48px" }}>
            {this.props.labels.print_preview || "Print preview"}
            <IconButton
              aria-label="Close print preview"
              onClick={this.closePrintPreview}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ height: "80vh", padding: 0 }}>
            {transferAgreementPdfUrl && (
              <object
                data={
                  transferAgreementPdfUrl +
                  "#toolbar=1&navpanes=0&scrollbar=1&view=FitH"
                }
                type="application/pdf"
                width="100%"
                height="100%"
              >
                <p style={{ padding: "16px" }}>
                  Your browser does not support PDFs. Please download the PDF to
                  view it:
                  <a href={transferAgreementPdfUrl}>Download PDF</a>.
                </p>
              </object>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.openPdfInNewTab}>
              <OpenInNewRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.open_pdf || "Open PDF"}
            </Button>
            <Button
              onClick={this.handlePrint}
              variant="contained"
              disabled={!this.state.printFrameLoaded}
              sx={{ backgroundColor: "#273A60" }}
            >
              <PrintRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.print || "Print"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
